import performanceMeasurement, {
  type Mark,
  type MetricName,
} from '@/constants/performance'
import { round } from 'lodash-es'
import { isE2ETest, createPerformanceResultEvent } from '../../../../e2e/types'

type Measurement = {
  label: string
  duration: number
}

export type Measurements = Measurement[]

const measurements: Measurements = []

/** The performance measurement is considered finished when the results have been displayed */
let measurementIsFinished = false

export function addMark(mark: Mark) {
  if (!performanceMeasurement.enabled) return
  performance.mark(mark)
}

export function measure(metricName: MetricName) {
  if (!performanceMeasurement.enabled) return

  const metric = performanceMeasurement.metric[metricName]

  if (!performanceMarkExists(metric.from)) {
    warnMissingPerformanceMark(metricName, metric.from)
    return
  }

  if (!performanceMarkExists(metric.to)) {
    warnMissingPerformanceMark(metricName, metric.to)
    return
  }

  const measurement = performance.measure(metric.name, metric.from, metric.to)
  /** Duration in ms */
  const duration = measurement.duration

  storeMeasure({
    metricName,
    duration,
  })

  // console.log(`${metric.name}: ${durationToSeconds(duration)}s`)
}

function performanceMarkExists(mark: Mark) {
  return performance.getEntriesByName(mark, 'mark').length > 0
}

function warnMissingPerformanceMark(metricName: MetricName, mark: Mark) {
  if (!import.meta.env.DEV) return

  console.warn(
    `Skipping performance measurement "${metricName}" because mark "${mark}" does not exist.`
  )
}

function storeMeasure(options: { metricName: MetricName; duration: number }) {
  measurements.push({
    label: options.metricName,
    duration: options.duration,
  })
}

export function durationToSeconds(duration: number) {
  return round(duration / 1000, 2)
}

export function showPerformanceResult() {
  if (!performanceMeasurement.enabled) return
  if (measurementIsFinished) return

  measurementIsFinished = true

  const table = {}

  measurements.forEach((item) => {
    table[item.label] = `${durationToSeconds(item.duration)}s`
  })

  console.table(table)

  hooksAfterResult.forEach((callback) => {
    callback(measurements)
  })

  if (isE2ETest(window)) {
    window.dispatchEvent(createPerformanceResultEvent(measurements))
  }
}

const hooksAfterResult = new Set<(result: typeof measurements) => void>()

export function registerHookAfterResult(
  callback: (result: typeof measurements) => void
) {
  if (measurementIsFinished) {
    throw new Error(
      'Registering a after result hook is no longer possible, the measurement is already finished'
    )
  }

  hooksAfterResult.add(callback)
}
